import type { SavedViewDefinitionV2 } from '@/utils/savedViewDefinition';

/**
 * Wire types for saved views (SAU-310). Kept in a plain module with no API
 * imports, the way `utils/favoriteFilters.ts` is, so the types and any pure
 * helpers stay trivially unit-testable.
 */

/** One of the caller's own saved views, as the server returns it. */
export interface SavedViewRecord {
  id: number;
  entity: string;
  name: string;
  definition: SavedViewDefinitionV2;
  /** Set when this view was imported from the library; purely informational. */
  forked_from_shared_saved_view_id: number | null;
  /** An icon key from SAVED_VIEW_ICONS, or null for the bookmark. Optional only so older fixtures still type-check. */
  icon?: string | null;
  /** A colour key from SAVED_VIEW_COLORS, or null for lime. */
  color?: string | null;
  created_at: string;
  updated_at: string;
}

/** A published view: a library row, and what a `?s=` link resolves to. */
export interface SharedSavedViewRecord {
  id: number;
  share_key: string;
  entity: string;
  name: string;
  /**
   * Present on the share-link resolver, ABSENT on the library listing.
   *
   * The listing returns up to a hundred rows and each definition can reach the
   * server's 64KB ceiling, so it stopped sending them: the dialog renders names and
   * row buttons and has never read one. Import does not need it either — the server
   * copies the definition from its own row. Anything that actually reads a definition
   * is working from a resolved link, so it has one.
   */
  definition?: SavedViewDefinitionV2;
  /** Absent unless the owner relation was loaded; a departed colleague still resolves. */
  shared_by_name?: string | null;
  /** Copied from the private view when it was published, and frozen there. */
  icon?: string | null;
  color?: string | null;
  /** The delete policy's answer for this caller: own rows, or any row for a developer. */
  can_delete: boolean;
  created_at: string;
}

/**
 * Trim-only normalisation, matching the rule the store used before views moved
 * to the server. Comparison folds case and accents on top of this.
 */
export const normalizeViewName = (name: string): string => name.trim();

/**
 * The comparison the SERVER's unique index makes, approximated closely enough to
 * catch a collision before the round-trip.
 *
 * `saved_views.name` is utf8mb4_unicode_ci, which folds case AND accents AND
 * trailing spaces — so "Cafe tracks" and "Café tracks" are one name to MySQL.
 * Folding only case here is what let a Save-as-new sail past this guard and match
 * an existing row on the server.
 *
 * This is deliberately an APPROXIMATION and not the authority. A collation folds
 * more than diacritics (ß/ss, ø/o, and a long tail besides), and re-implementing
 * one in JavaScript is a losing game. `SavedViewController::store` therefore asks
 * MySQL and refuses a collision with a 409 rather than overwriting; this exists so
 * the common case is caught while the user is still typing, not so the server can
 * trust it.
 */
//
// The INTERSECTION of `\p{M}` and `\p{Diacritic}` — a combining mark that is
// also a diacritic — because each alone folds things MySQL keeps. `\p{Diacritic}`
// on its own also matches spacing characters such as ^, the backtick, ´, · and
// the katakana long-vowel mark ー, so "ラーメン" was refused as a duplicate of
// "ラメン". `\p{M}` on its own also matches the vowel signs of Devanagari and
// Thai, which the collation gives a primary weight, so "काम" would be refused as
// a duplicate of "कम". Accents (é), dakuten (ガ) and the like are both, and fold.
const foldViewName = (name: string): string =>
  normalizeViewName(name)
    .normalize('NFD')
    .replace(/(?=\p{M})\p{Diacritic}/gu, '')
    .toLocaleLowerCase();

/** Trimmed equality, folding case and accents the way the unique index does. */
export const viewNamesMatch = (a: string, b: string): boolean =>
  foldViewName(a) === foldViewName(b);

/**
 * Case-insensitive, trimmed name-collision check across a list of saved views.
 *
 * This has to stay SYNCHRONOUS. The sidebar's New saved view dialog calls it
 * on every keystroke to enable or disable its Save button. A server round-trip here would return a Promise, which
 * is truthy, so every name would read as a conflict and the button would never
 * enable. The list it checks is the TanStack query's cached data, which the
 * component already has.
 *
 * An empty trimmed name returns FALSE on purpose: empty is rejected upstream as
 * its own 'empty_name' outcome with a different message. Returning true here
 * would tell the user "that name already exists" when they typed nothing.
 */
export function hasViewNameConflict(
  views: readonly SavedViewRecord[],
  name: string,
  excludeId?: number,
): boolean {
  const trimmed = normalizeViewName(name);
  if (trimmed === '') {
    return false;
  }
  return views.some((view) => view.id !== excludeId && viewNamesMatch(view.name, trimmed));
}
